import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import heroImage from '../assets/Harsh in Black.jpg';
import TextPressure from '../blocks/TextAnimations/TextPressure/TextPressure.jsx';


// Styled Components
const HeroSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 4rem 2rem;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.background};
  overflow: hidden;
  position: relative;

  @media (min-width: 768px) {
    flex-direction: row;
    text-align: left;
  }
`;

const HeroText = styled.div`
  flex: 1;
  padding: 1rem;
  z-index: 1;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.accent};
  position: relative;
  height: 100px;
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.color};

  .highlight {
    color: ${({ theme }) => theme.accent};
    font-weight: bold;
    cursor: pointer;
  }
`;

const CallToAction = styled(motion.a)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  color: ${({ theme }) => theme.background};
  background-color: ${({ theme }) => theme.accent};
  text-decoration: none;
  border-radius: 5px;
  transition: background-color 0.3s ease, transform 0.3s ease;
  animation: heartbeat 1.5s infinite;

  &:hover {
    background-color: ${({ theme }) => theme.accentHover};
    transform: scale(1.1) rotate(5deg);
  }
  &:active {
    transform: scale(0.95) rotate(0deg);
  }

  @keyframes heartbeat {
    0% { transform: scale(1); }
    20% { transform: scale(1.1); }
    40% { transform: scale(1); }
    60% { transform: scale(1.1); }
    80% { transform: scale(1); }
    100% { transform: scale(1); }
  }
`;

const Particle = styled(motion.div)`
  position: absolute;
  width: 15px;
  height: 15px;
  background-color: ${({ theme }) => theme.accent};
  clip-path: polygon(
    50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%,
    50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%
  );
  opacity: 0.7;
  animation: float 5s infinite;

  @keyframes float {
    0% { transform: translateY(0) scale(1); }
    50% { transform: translateY(-100px) scale(1.2); }
    100% { transform: translateY(0) scale(1); }
  }
`;

const HeroImage = styled(motion.img)`
  width: 250px;
  height: 250px;
  border-radius: 50%;
  object-fit: cover;
  margin-top: 2rem;
  z-index: 1;
  filter: drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.2));
  position: relative;

  @media (min-width: 768px) {
    margin-top: 0;
    margin-left: 2rem;
  }
`;

const generateParticles = (num) =>
  Array.from({ length: num }, (_, i) => (
    <Particle
      key={i}
      style={{
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
      }}
    />
  ));

const heroVariants = {
  hidden: { opacity: 0, y: -50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const highlightVariants = {
  hover: { scale: 1.2, color: '#FF5733', transition: { duration: 0.3 } },
};

const Hero = () => (
  <HeroSection variants={heroVariants} initial="hidden" animate="visible">
    <HeroText>
      <HeroTitle
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <TextPressure
          text="Welcome to My Portfolio"
          flex={true}
          alpha={false}
          stroke={false}
          width={true}
          weight={true}
          italic={true}
          textColor="#ffffff"
          strokeColor="#ff0000"
          minFontSize={36}
        />
      </HeroTitle>

      <HeroSubtitle
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        I'm{' '}
        <motion.span
          className="highlight"
          variants={highlightVariants}
          whileHover="hover"
        >
          Harsh Dhama
        </motion.span>
        , a dedicated{' '}
        <motion.span
          className="highlight"
          variants={highlightVariants}
          whileHover="hover"
        >
          Full Stack Web Developer
        </motion.span>{' '}
        with industry experience in software development. I am passionate
        about creating dynamic and efficient web applications and also have
        a strong interest in{' '}
        <motion.span
          className="highlight"
          variants={highlightVariants}
          whileHover="hover"
        >
          cybersecurity
        </motion.span>
        . Explore my work and feel free to get in touch!
      </HeroSubtitle>

      <CallToAction
        href="#contact"
        initial={{ scale: 1 }}
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.9 }}
      >
        Contact Me
      </CallToAction>
    </HeroText>

    <HeroImage
      src={heroImage}
      alt="Harsh Dhama"
      animate={{ y: [0, -20, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      whileHover={{ scale: 1.1 }}
    />

    {generateParticles(20)}
  </HeroSection>
);

export default Hero;
